import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { SettingsService } from '../settings/settings.service';
import { TBlkCxpRole } from '../../types/settingsDataModule';
import {
  permissionsConfig,
} from '../../../utils/roles.config';
import {
  TPermissionCategory,
  TPermissionKey,
} from '../../../utils/rolesDataModule';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private settingsService = inject(SettingsService);

  constructor() {}

  /**
   * Check if the current user has permission to perform a specific action
   * @param category - The permission category (e.g., 'USER_MANAGEMENT')
   * @param permission - The specific permission (e.g., 'create-users')
   * @returns Observable<boolean> - true if user has permission, false otherwise
   */
  hasPermission(
    category: TPermissionCategory,
    permission: TPermissionKey
  ): Observable<boolean> {
    return this.settingsService.getUserRoles.pipe(
      map((userRoles: TBlkCxpRole[]) => {
        return this.checkPermission(userRoles, category, permission);
      })
    );
  }

  /**
   * Synchronous version of hasPermission for use in guards and resolvers
   * @param userRoles - Array of user roles
   * @param category - The permission category
   * @param permission - The specific permission
   * @returns boolean - true if user has permission, false otherwise
   */
  checkPermission(
    userRoles: TBlkCxpRole[],
    category: TPermissionCategory,
    permission: TPermissionKey
  ): boolean {
    if (!userRoles || userRoles.length === 0) {
      return false;
    }

    const categoryPermissions = permissionsConfig[category];
    if (!categoryPermissions) {
      return false;
    }

    const allowedRoles = (categoryPermissions as any)[permission];
    if (!allowedRoles || !Array.isArray(allowedRoles)) {
      return false;
    }

    // Check if user has any of the required roles
    return userRoles.some((userRole) => allowedRoles.includes(userRole));
  }

  /**
   * Check if user has any of the specified permissions
   * @param category - The permission category
   * @param permissions - Array of permissions to check
   * @returns Observable<boolean> - true if user has at least one permission
   */
  hasAnyPermission(
    category: TPermissionCategory,
    permissions: TPermissionKey[]
  ): Observable<boolean> {
    return this.settingsService.getUserRoles.pipe(
      map((userRoles: TBlkCxpRole[]) => {
        return permissions.some((permission) =>
          this.checkPermission(userRoles, category, permission)
        );
      })
    );
  }

  /**
   * Check if user has all of the specified permissions
   * @param category - The permission category
   * @param permissions - Array of permissions to check
   * @returns Observable<boolean> - true if user has all permissions
   */
  hasAllPermissions(
    category: TPermissionCategory,
    permissions: TPermissionKey[]
  ): Observable<boolean> {
    return this.settingsService.getUserRoles.pipe(
      map((userRoles: TBlkCxpRole[]) => {
        return permissions.every((permission) =>
          this.checkPermission(userRoles, category, permission)
        );
      })
    );
  }

  /**
   * Get all permissions for a specific category that the user has access to
   * @param category - The permission category
   * @returns Observable<TPermissionKey[]> - Array of permissions user has access to
   */
  getUserPermissionsForCategory(
    category: TPermissionCategory
  ): Observable<TPermissionKey[]> {
    return this.settingsService.getUserRoles.pipe(
      map((userRoles: TBlkCxpRole[]) => {
        const categoryPermissions = permissionsConfig[category];
        if (!categoryPermissions) {
          return [];
        }

        const userPermissions: TPermissionKey[] = [];
        Object.entries(categoryPermissions).forEach(([key, roles]) => {
          if (
            userRoles.some((userRole) =>
              (roles as TBlkCxpRole[]).includes(userRole)
            )
          ) {
            userPermissions.push(key as TPermissionKey);
          }
        });

        return userPermissions;
      })
    );
  }

  /**
   * Check if user is a super admin
   * @returns Observable<boolean>
   */
  isSuperAdmin(): Observable<boolean> {
    return this.settingsService.getUserRoles.pipe(
      map((userRoles: TBlkCxpRole[]) => {
        return userRoles.includes('SUPER_ADMIN');
      })
    );
  }

  /**
   * Check if user is an administrator
   * @returns Observable<boolean>
   */
  isAdministrator(): Observable<boolean> {
    return this.settingsService.getUserRoles.pipe(
      map((userRoles: TBlkCxpRole[]) => {
        return (
          userRoles.includes('SUPER_ADMIN') ||
          userRoles.includes('ROLE_BLKKCXP_ADMINISTRATOR')
        );
      })
    );
  }
}
